import { api, unwrap } from "@lumberset/api-client";
import {
  Badge,
  Button,
  Container,
  Drawer,
  Field,
  Input,
  Prompt,
  SectionHeader,
  Switch,
  Textarea,
  toast,
} from "@lumberset/ui";
import { Envelope } from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useDeferredValue, useRef, useState } from "react";
import { Crumbs } from "../../components/crumbs";
import { emailTemplatesQuery } from "../../lib/queries";

export const Route = createFileRoute("/_app/email-templates")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(emailTemplatesQuery),
  component: EmailTemplates,
});

type Template = Awaited<
  ReturnType<NonNullable<typeof emailTemplatesQuery.queryFn>>
>["templates"][number];

/** The wording as the Admin edits it: paragraphs separated by blank lines. */
interface Wording {
  subject: string;
  heading: string;
  body: string;
  actionLabel: string;
  footnote: string;
}

const wordingOf = (template: Template): Wording =>
  template.override
    ? {
        subject: template.override.subject,
        heading: template.override.heading,
        body: template.override.body,
        actionLabel: template.override.actionLabel ?? "",
        footnote: template.override.footnote ?? "",
      }
    : {
        subject: template.defaults.subject,
        heading: template.defaults.heading,
        body: template.defaults.body.join("\n\n"),
        actionLabel: template.defaults.actionLabel ?? "",
        footnote: template.defaults.footnote ?? "",
      };

const toJson = (wording: Wording) => ({
  subject: wording.subject,
  heading: wording.heading,
  body: wording.body,
  actionLabel: wording.actionLabel.trim() || null,
  footnote: wording.footnote.trim() || null,
});

function EmailTemplates() {
  const { data } = useSuspenseQuery(emailTemplatesQuery);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const editing = data.templates.find((template) => template.key === editingKey) ?? null;

  return (
    <>
      <Crumbs trail={[{ label: "Email templates" }]} />
      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Email templates"
          description="The wording of every email the platform sends. Change one, or leave the platform's own words."
        />
        <ul className="divide-y divide-ui-border-base">
          {data.templates.map((template) => (
            <li key={template.key}>
              <button
                type="button"
                className="transition-fg flex w-full cursor-pointer items-center gap-x-3 px-6 py-3.5 text-left outline-none hover:bg-ui-bg-base-hover focus-visible:shadow-borders-focus"
                onClick={() => setEditingKey(template.key)}
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-ui-bg-subtle text-ui-fg-subtle">
                  <Envelope />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="txt-compact-small-plus block truncate text-ui-fg-base">
                    {template.name}
                  </span>
                  <span className="txt-compact-xsmall block truncate text-ui-fg-muted">
                    {template.description}
                  </span>
                </span>
                {template.override?.enabled ? (
                  <Badge color="blue">Customized</Badge>
                ) : template.override ? (
                  <Badge color="grey">Customized, off</Badge>
                ) : (
                  <Badge color="grey">Default</Badge>
                )}
              </button>
            </li>
          ))}
        </ul>
      </Container>

      <Drawer open={editing !== null} onOpenChange={(open) => !open && setEditingKey(null)}>
        <Drawer.Content className="sm:max-w-[720px]">
          {editing ? (
            <TemplateEditor
              key={editing.key}
              template={editing}
              onClose={() => setEditingKey(null)}
            />
          ) : null}
        </Drawer.Content>
      </Drawer>
    </>
  );
}

function TemplateEditor({ template, onClose }: { template: Template; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [wording, setWording] = useState<Wording>(() => wordingOf(template));
  const [enabled, setEnabled] = useState(template.override?.enabled ?? true);
  const [confirmReset, setConfirmReset] = useState(false);
  const focused = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const previewInput = useDeferredValue(wording);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin", "email-templates"] });
  const fail = (error: Error) => toast.error(error.message);

  const set = (key: keyof Wording, value: string) =>
    setWording((current) => ({ ...current, [key]: value }));

  /** Puts `{{name}}` where the cursor last was, or at the end of the body. */
  function insertVariable(name: string) {
    const element = focused.current;
    const token = `{{${name}}}`;
    if (!element) {
      set(
        "body",
        `${wording.body}${wording.body.endsWith(" ") || !wording.body ? "" : " "}${token}`,
      );
      return;
    }
    const key = element.name as keyof Wording;
    const start = element.selectionStart ?? element.value.length;
    const end = element.selectionEnd ?? start;
    const next = `${element.value.slice(0, start)}${token}${element.value.slice(end)}`;
    set(key, next);
    requestAnimationFrame(() => {
      element.focus();
      element.setSelectionRange(start + token.length, start + token.length);
    });
  }

  const preview = useQuery({
    queryKey: ["admin", "email-templates", template.key, "preview", previewInput],
    queryFn: () =>
      unwrap(
        api.admin.email.templates[":key"].preview.$post({
          param: { key: template.key },
          json: toJson(previewInput),
        }),
      ),
    enabled: Boolean(previewInput.subject && previewInput.heading && previewInput.body),
    retry: false,
    placeholderData: (previous) => previous,
  });

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.admin.email.templates[":key"].$put({
          param: { key: template.key },
          json: { ...toJson(wording), enabled },
        }),
      ),
    onSuccess: async () => {
      await refresh();
      toast.success("Wording saved", {
        description: enabled
          ? "New emails use it from now on."
          : "Saved, but the default is in use.",
      });
      onClose();
    },
    onError: fail,
  });

  const test = useMutation({
    mutationFn: () =>
      unwrap(
        api.admin.email.templates[":key"].test.$post({
          param: { key: template.key },
          json: toJson(wording),
        }),
      ),
    onSuccess: ({ sentTo }) => toast.success("Test sent", { description: `Check ${sentTo}.` }),
    onError: fail,
  });

  const reset = useMutation({
    mutationFn: () =>
      unwrap(api.admin.email.templates[":key"].$delete({ param: { key: template.key } })),
    onSuccess: async () => {
      await refresh();
      toast.success("Back to the default wording");
      onClose();
    },
    onError: fail,
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate();
  }

  /** Remembers which field has the cursor, so a variable chip can be inserted there. */
  const track = {
    onFocus: (event: { target: HTMLInputElement | HTMLTextAreaElement }) => {
      focused.current = event.target;
    },
  };

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <Drawer.Header>
        <Drawer.Title>{template.name}</Drawer.Title>
        <Drawer.Description>{template.description}</Drawer.Description>
      </Drawer.Header>
      <Drawer.Body className="flex flex-col gap-y-5">
        <div className="flex flex-col gap-y-2">
          <p className="txt-compact-small-plus text-ui-fg-base">Variables</p>
          <p className="txt-small text-ui-fg-subtle">
            Click one to insert it where you are typing. It is filled in when the email is sent.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {template.variables.map((variable) => (
              <button
                key={variable.name}
                type="button"
                title={`${variable.description}. Example: ${variable.example}`}
                className="cursor-pointer outline-none focus-visible:shadow-borders-focus"
                onClick={() => insertVariable(variable.name)}
              >
                <Badge color="blue" size="xsmall" className="font-mono">
                  {`{{${variable.name}}}`}
                </Badge>
              </button>
            ))}
          </div>
        </div>

        <Field label="Subject">
          {(control) => (
            <Input
              {...control}
              {...track}
              name="subject"
              required
              maxLength={200}
              value={wording.subject}
              onChange={(event) => set("subject", event.target.value)}
            />
          )}
        </Field>
        <Field label="Heading">
          {(control) => (
            <Input
              {...control}
              {...track}
              name="heading"
              required
              maxLength={200}
              value={wording.heading}
              onChange={(event) => set("heading", event.target.value)}
            />
          )}
        </Field>
        <Field
          label="Body"
          hint="Leave a blank line between paragraphs. A paragraph whose variables are all empty is left out."
        >
          {(control) => (
            <Textarea
              {...control}
              {...track}
              name="body"
              required
              rows={7}
              maxLength={5000}
              value={wording.body}
              onChange={(event) => set("body", event.target.value)}
            />
          )}
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="Button label"
            optional
            hint="The button links where this email always links."
          >
            {(control) => (
              <Input
                {...control}
                {...track}
                name="actionLabel"
                maxLength={80}
                value={wording.actionLabel}
                onChange={(event) => set("actionLabel", event.target.value)}
              />
            )}
          </Field>
          <Field label="Footnote" optional>
            {(control) => (
              <Input
                {...control}
                {...track}
                name="footnote"
                maxLength={500}
                value={wording.footnote}
                onChange={(event) => set("footnote", event.target.value)}
              />
            )}
          </Field>
        </div>

        <div className="flex items-center justify-between gap-x-4 rounded-lg bg-ui-bg-subtle px-4 py-3">
          <label htmlFor="template-enabled" className="min-w-0 cursor-pointer">
            <span className="txt-compact-small-plus block text-ui-fg-base">Use this wording</span>
            <span className="txt-compact-xsmall block text-ui-fg-muted">
              Off keeps your words saved but sends the platform's own.
            </span>
          </label>
          <Switch id="template-enabled" checked={enabled} onCheckedChange={setEnabled} />
        </div>

        <div className="flex flex-col gap-y-2">
          <p className="txt-compact-small-plus text-ui-fg-base">Preview with example values</p>
          {preview.isError ? (
            <p className="txt-small rounded-lg bg-ui-tag-red-bg px-4 py-3 text-ui-tag-red-text">
              {preview.error.message}
            </p>
          ) : preview.data ? (
            <div className="flex flex-col gap-y-3 rounded-lg border border-ui-border-base bg-ui-bg-base px-5 py-4">
              <p className="txt-compact-xsmall text-ui-fg-muted">Subject: {preview.data.subject}</p>
              <p className="h2-core text-ui-fg-base">{preview.data.heading}</p>
              {preview.data.paragraphs.map((paragraph, index) => (
                <p
                  key={`${index}:${paragraph}`}
                  className="txt-medium whitespace-pre-line text-ui-fg-subtle"
                >
                  {paragraph}
                </p>
              ))}
              {preview.data.action ? (
                <span className="txt-compact-small-plus w-fit rounded-md bg-ui-button-inverted px-3 py-1.5 text-ui-contrast-fg-primary">
                  {preview.data.action.label}
                </span>
              ) : null}
              {preview.data.footnote ? (
                <p className="txt-small text-ui-fg-muted">{preview.data.footnote}</p>
              ) : null}
            </div>
          ) : (
            <p className="txt-small text-ui-fg-muted">
              Fill in a subject, heading and body to see it.
            </p>
          )}
        </div>
      </Drawer.Body>
      <Drawer.Footer className="flex-wrap justify-between gap-y-2">
        <div className="flex items-center gap-x-2">
          <Button
            type="button"
            size="small"
            variant="transparent"
            disabled={!template.override}
            onClick={() => setConfirmReset(true)}
          >
            Reset to default
          </Button>
          <Button
            type="button"
            size="small"
            variant="secondary"
            isLoading={test.isPending}
            onClick={() => test.mutate()}
          >
            Send me a test
          </Button>
        </div>
        <Button type="submit" size="small" isLoading={save.isPending}>
          Save
        </Button>
      </Drawer.Footer>

      <Prompt open={confirmReset} onOpenChange={setConfirmReset}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Reset to the default wording?</Prompt.Title>
            <Prompt.Description>
              Your words for this email are deleted and the platform's own wording is used again.
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Keep mine</Prompt.Cancel>
            <Prompt.Action onClick={() => reset.mutate()}>Reset</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </form>
  );
}
